import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, Zap, Clock, CheckCircle2, ArrowLeft } from "lucide-react";

const PrelimsCSATPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState("quant");
  const [result, setResult] = useState(null);

  const isPro = user?.role === "PRO";

  const handleSolve = async () => {
    if (!question.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a CSAT question"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/csat/solve`,
        {
          question,
          question_type: questionType
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: isPro ? "Solution generated with shortcuts!" : "Answer provided"
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to solve question"
      });
    } finally {
      setLoading(false);
    }
  };

  const sampleQuestions = {
    quant: `A train 150 meters long is running at a speed of 54 km/hr. How long will it take to pass a platform 250 meters long?

A) 20 seconds
B) 24 seconds
C) 26.67 seconds
D) 30 seconds`,
    
    logical: `If 'COMPUTER' is coded as 'RFUVQNPC', then 'MACHINE' will be coded as:

A) NBDIJOF
B) LZBIHOD
C) NZDIFJM
D) LBCHFMD`,
    
    di: `Study the following table showing the production (in thousands) of 5 companies over 4 years:

Company   2020   2021   2022   2023
A         120    145    167    189
B         98     115    132    148
C         156    178    201    225

What is the percentage increase in production of Company A from 2020 to 2023?

A) 57.5%
B) 62.5%
C) 55%
D) 60%`,
    
    comprehension: `Read the passage and answer:

"The concept of sustainable development emerged in the 1980s as a response to growing environmental concerns. It emphasizes meeting present needs without compromising future generations' ability to meet their needs."

Which of the following best describes sustainable development?

A) Economic growth at any cost
B) Balance between present and future needs
C) Environmental protection only
D) Industrial development`
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CSAT Solver</h1>
          <p className="text-sm text-slate-600">
            Solve aptitude questions with topper-grade shortcuts
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-slate-200"}>
          {isPro ? "⭐ PRO" : "🔓 FREE"}
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">
                {isPro ? "PRO Features:" : "FREE Version:"}
              </p>
              {isPro ? (
                <ul className="text-sm text-slate-700 space-y-1">
                  <li>• Step-by-step solution with detailed explanation</li>
                  <li>• Shortcut method for quick solving</li>
                  <li>• Alternate approaches</li>
                  <li>• Time-saving tricks</li>
                  <li>• Similar question patterns</li>
                </ul>
              ) : (
                <p className="text-sm text-slate-700">
                  FREE users get final answer only. Upgrade to PRO for complete solutions with shortcuts.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="questionType">Question Type</Label>
              <select
                id="questionType"
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="quant">Quantitative (Math)</option>
                <option value="logical">Logical Reasoning</option>
                <option value="di">Data Interpretation</option>
                <option value="comprehension">Comprehension</option>
                <option value="venn">Venn Diagrams</option>
                <option value="pattern">Pattern Recognition</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="question">Question</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuestion(sampleQuestions[questionType] || "")}
                  disabled={loading}
                >
                  Load Sample
                </Button>
              </div>
              <Textarea
                id="question"
                placeholder="Paste your CSAT question here with options (if multiple choice)"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={loading}
                rows={15}
              />
            </div>

            <Button
              onClick={handleSolve}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Solving...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Solve Question
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Solution Section */}
        <div className="space-y-4">
          {result && (
            <>
              {/* Final Answer */}
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-slate-900">Answer</h3>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{result.answer}</p>
                </CardContent>
              </Card>

              {/* PRO: Step-by-Step Solution */}
              {isPro && result.step_by_step && (
                <Card>
                  <CardContent className="p-6 space-y-3">
                    <h3 className="font-semibold text-slate-900">Step-by-Step Solution</h3>
                    <div className="space-y-2">
                      {result.step_by_step.map((step, idx) => (
                        <div key={idx} className="flex gap-3">
                          <Badge variant="outline" className="shrink-0 h-fit">
                            {idx + 1}
                          </Badge>
                          <p className="text-sm text-slate-700">{step}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* PRO: Shortcut Method */}
              {isPro && result.shortcut_method && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-slate-900">Shortcut Method</h3>
                    </div>
                    <p className="text-sm text-slate-700">{result.shortcut_method}</p>
                  </CardContent>
                </Card>
              )}

              {/* PRO: Alternate Method */}
              {isPro && result.alternate_method && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-3">Alternate Approach</h3>
                    <p className="text-sm text-slate-700">{result.alternate_method}</p>
                  </CardContent>
                </Card>
              )}

              {/* PRO: Time-Saving Tricks */}
              {isPro && result.time_saving_tricks && result.time_saving_tricks.length > 0 && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-900">Time-Saving Tricks</h3>
                    </div>
                    <ul className="space-y-2">
                      {result.time_saving_tricks.map((trick, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="text-amber-600">•</span>
                          {trick}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* PRO: Difficulty & Similar Patterns */}
              {isPro && (result.difficulty || result.similar_pattern) && (
                <Card>
                  <CardContent className="p-6 space-y-3">
                    {result.difficulty && (
                      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600">Difficulty Level</p>
                        <Badge className={
                          result.difficulty === "Hard" ? "bg-red-500" :
                          result.difficulty === "Medium" ? "bg-orange-500" : "bg-green-500"
                        }>
                          {result.difficulty}
                        </Badge>
                      </div>
                    )}
                    {result.similar_pattern && (
                      <div>
                        <p className="text-sm font-semibold text-slate-900 mb-1">Similar Question Pattern:</p>
                        <p className="text-sm text-slate-600">{result.similar_pattern}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* FREE: Upgrade CTA */}
              {!isPro && result.message && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-amber-900">{result.message}</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Placeholder */}
          {!result && !loading && (
            <Card className="border-dashed">
              <CardContent className="p-12">
                <div className="text-center text-slate-400">
                  <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Solution will appear here</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrelimsCSATPage;
